import type {
  CurrencyFormatOptions,
  DatatableSettingsState,
  TagStyle,
} from 'components/datatable/datatable.types';
import { createColumnGetter } from 'components/datatable/utils';
import {
  COLUMN_BILLABLE,
  COLUMN_CLIENT,
  COLUMN_DAY,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_LINKED_WORK_ID,
  COLUMN_LINKED_WORK_STATUS,
  COLUMN_LINKED_WORK_TITLE,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_NAME,
  COLUMN_NOTES,
  COLUMN_PROJECT,
  COLUMN_STATE,
  COLUMN_TAGS,
} from 'constants/filters';
import type { MutableRefObject } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { TimesheetsDatatableRow } from 'redux/modules/superreports-datatable.types';
import { getTagEntities } from 'redux/modules/tags.selectors';
import {
  getLinkedWorkIdColumn,
  getLinkedWorkStatusColumn,
  getLinkedWorkTitleColumn,
} from 'utils/linked-work';

const getColumnDef = createColumnGetter<TimesheetsDatatableRow>();

export const useTimesheetColumnDefinitions = (
  lastSelectedRowIndex: MutableRefObject<null | number>,
  settings: DatatableSettingsState,
  datatableRows: Array<TimesheetsDatatableRow>,
  hasTicIntegration: boolean = false
) => {
  const tagsById = useSelector(getTagEntities);
  const timeStyle = settings.timeStyle || 'traditional';
  const currencyFormat = settings.currencyFormat as CurrencyFormatOptions | undefined;
  const tagStyle = settings.tagStyle as TagStyle | undefined;
  const parentTagIds = useMemo(() => {
    const tagIds = new Set<number>();
    datatableRows.forEach(({ tags }) => {
      tags.forEach(t => {
        if (t.parent) {
          tagIds.add(t.parent.id);
        }
      });
    });
    return Array.from(tagIds);
  }, [datatableRows]);

  // Calculate the maximum number of linked work items across all rows
  const maxLinkedWorkItems = useMemo(() => {
    if (!hasTicIntegration) return 0;
    return Math.max(0, ...datatableRows.map(row => row.linkedWorkItems?.length || 0));
  }, [datatableRows, hasTicIntegration]);

  const columns = useMemo(
    () => [
      getColumnDef('selection', 'id', 'selection', { lastSelectedRowIndex }),
      getColumnDef('date', 'date', COLUMN_DAY),
      getColumnDef('user', 'user', COLUMN_NAME), // @todo should we create a COLUMN_USER for this in the new setup?
      getColumnDef('project', 'project', COLUMN_PROJECT, { label: 'Client', mandatory: true }),
      ...(tagStyle?.tagStructure === 'consolidated'
        ? [getColumnDef('tags', 'tags', COLUMN_TAGS, { tagStyle })]
        : parentTagIds.map(parentTagId =>
            getColumnDef('tagList', 'tags', `tag-${parentTagId}`, {
              tag: tagsById[parentTagId],
            })
          )),
      {
        ...getColumnDef('text', 'project.client.name', COLUMN_CLIENT, {
          label: 'Client',
          mandatory: true,
        }),
        enableSorting: true,
      },
      {
        ...getColumnDef('text', 'billable', COLUMN_BILLABLE, {
          label: 'Billable',
          excludeAggregateView: true,
        }),
        enableSorting: true,
      },

      getColumnDef('state', 'state', COLUMN_STATE),
      getColumnDef('duration', 'loggedMinutes', COLUMN_LOGGED_HOURS, {
        label: 'Time',
        timeStyle,
        mandatory: true,
      }),
      getColumnDef('money', 'loggedMoney', COLUMN_LOGGED_MONEY, { label: 'Money', currencyFormat }),
      getColumnDef('duration', 'plannedMinutes', COLUMN_ESTIMATED_HOURS, {
        label: 'Planned time',
        timeStyle,
      }),
      getColumnDef('money', 'plannedMoney', COLUMN_ESTIMATED_MONEY, {
        label: 'Planned money',
        currencyFormat,
      }),
      { ...getColumnDef('text', 'note', COLUMN_NOTES, { label: 'Note' }), enableSorting: false },
      ...(hasTicIntegration && maxLinkedWorkItems > 0
        ? Array.from({ length: maxLinkedWorkItems }, (_, i) => [
            getColumnDef('linkedWork', 'linkedWorkItems', getLinkedWorkIdColumn(i), {
              label:
                maxLinkedWorkItems === 1 ? 'Linked Work Item ID' : `Linked Work Item ${i + 1} ID`,
              field: 'external_code',
              index: i,
              parentColumnId: COLUMN_LINKED_WORK_ID,
            }),
            getColumnDef('linkedWork', 'linkedWorkItems', getLinkedWorkTitleColumn(i), {
              label:
                maxLinkedWorkItems === 1
                  ? 'Linked Work Item Title'
                  : `Linked Work Item ${i + 1} Title`,
              field: 'title',
              index: i,
              parentColumnId: COLUMN_LINKED_WORK_TITLE,
            }),
            getColumnDef('linkedWork', 'linkedWorkItems', getLinkedWorkStatusColumn(i), {
              label:
                maxLinkedWorkItems === 1
                  ? 'Linked Work Item Sync Status'
                  : `Linked Work Item ${i + 1} Sync Status`,
              field: 'status',
              index: i,
              parentColumnId: COLUMN_LINKED_WORK_STATUS,
            }),
          ]).flat()
        : []),
    ],
    [
      lastSelectedRowIndex,
      tagStyle,
      parentTagIds,
      timeStyle,
      currencyFormat,
      tagsById,
      hasTicIntegration,
      maxLinkedWorkItems,
    ]
  );

  return columns;
};
